import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import SEO, { organizationStructuredData } from '../components/SEO';

function HomePage() {
  return (
    <>
      <SEO
        structuredData={organizationStructuredData}
      />
      <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Navigation Header */}
      <header className="fixed top-0 w-full z-50 bg-black/50 backdrop-blur-md border-b border-gray-800">
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-red-500 to-yellow-500 bg-clip-text text-transparent">
              Jim Root Memorial
            </span>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <Link to="/jim-root" className="text-gray-300 hover:text-white transition-colors">Jim Root</Link>
            <Link to="/kusumoto" className="text-gray-300 hover:text-white transition-colors">Kusumoto</Link>
            <Link to="/register" className="text-gray-300 hover:text-white transition-colors">Registration</Link>
            <a href="#contact" className="text-gray-300 hover:text-white transition-colors">Contact</a>
          </div>

          <Link
            to="/register"
            className="bg-red-600 hover:bg-red-700 px-6 py-2 rounded-full font-semibold transition-colors"
          >
            Register Now
          </Link>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center px-6">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-black to-yellow-900/20" />

        {/* Animated gradient orb */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-red-600/30 to-yellow-600/30 rounded-full blur-3xl animate-pulse" />

        <div className="relative z-10 text-center max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6"
          >
            <span className="inline-flex items-center px-4 py-2 bg-gray-800/50 backdrop-blur-sm rounded-full text-sm text-gray-300">
              <span className="mr-2">📅</span>
              January 15-16, 2027
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6"
          >
            Prospect High School
            <br />
            <span className="bg-gradient-to-r from-red-500 to-yellow-500 bg-clip-text text-transparent">
              Wrestling Tournaments
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-400 mb-4 max-w-3xl mx-auto"
          >
            63rd Annual Jim Root Memorial & 10th Annual Kusumoto Klassic
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="text-base md:text-lg text-gray-400 mb-10 max-w-2xl mx-auto"
          >
            Two premier wrestling tournaments at Prospect High School, Saratoga CA.
            Boys tournament January 16th, Girls tournament January 15th.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex gap-4 justify-center flex-wrap"
          >
            <Link
              to="/register"
              className="bg-red-600 hover:bg-red-700 px-8 py-3 rounded-full font-semibold text-lg transition-all transform hover:scale-105"
            >
              Register Your Team
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Tournament Cards Section */}
      <section id="tournaments" className="relative py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-12"
          >
            Tournament Details
          </motion.h2>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          >
            {tournaments.map((tournament, index) => (
              <Link to={tournament.link} key={tournament.title}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.6 }}
                  className="group bg-gradient-to-b from-gray-900/50 to-gray-900/20 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 hover:border-red-500/50 transition-all cursor-pointer h-full"
                >
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform">
                    {tournament.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-white">
                    {tournament.title}
                  </h3>
                  <p className="text-gray-400 mb-4">
                    {tournament.description}
                  </p>
                  {tournament.details && (
                    <div className="text-sm text-gray-500 mb-4">
                      {tournament.details}
                    </div>
                  )}
                  <div className="text-sm text-red-400 group-hover:text-red-300 transition-colors">
                    Learn More →
                  </div>
                </motion.div>
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact & Sponsors Section */}
      <section id="contact" className="relative py-20 px-6 border-t border-gray-800">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold mb-6 text-white">Contact Information</h3>
              <div className="space-y-4 text-gray-400">
                <div>
                  <p className="font-semibold text-white mb-1">Email</p>
                  <a href="mailto:Jimrootwrestling@gmail.com" className="hover:text-red-500 transition-colors">
                    Jimrootwrestling@gmail.com
                  </a>
                </div>
                <div>
                  <p className="font-semibold text-white mb-1">Phone</p>
                  <a href="tel:6506197529" className="hover:text-red-500 transition-colors">
                    (650) 619-7529
                  </a>
                </div>
                <div>
                  <p className="font-semibold text-white mb-1">Registration</p>
                  <a
                    href="https://jimrootwrestling.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-red-500 transition-colors"
                  >
                    JimRootWrestling.com
                  </a>
                </div>
                <div>
                  <p className="font-semibold text-white mb-1">Venue</p>
                  <p>Prospect High School<br />Saratoga, CA</p>
                </div>
              </div>
            </motion.div>

            {/* Sponsors */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold mb-6 text-white">Proud Sponsors</h3>
              <div className="space-y-3 text-gray-400">
                <p className="text-lg">• Team Fluffy Wrestling</p>
                <p className="text-lg">• Lions Club</p>
                <p className="text-lg">• Distant Klash</p>
              </div>
              <p className="mt-6 text-sm text-gray-500">
                Thank you to our sponsors for supporting youth wrestling in our community.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-800">
        <div className="container mx-auto text-center text-gray-500">
          <p>© 2027 Jim Root Memorial Wrestling Tournament. All rights reserved.</p>
        </div>
      </footer>
    </div>
    </>
  );
}

const tournaments = [
  {
    icon: '🏆',
    title: 'Jim Root Memorial',
    description: '63rd Annual Boys Wrestling Tournament - Saturday, January 16th, 2027',
    details: '32-man double elimination brackets on 8 mats. NFHS sanctioned competition.',
    link: '/jim-root',
  },
  {
    icon: '🥇',
    title: 'Kusumoto Klassic',
    description: '10th Annual Girls Wrestling Tournament - Friday, January 15th, 2027',
    details: '16 or 32 person double elimination brackets on 4-5 mats. Presented by Distant Klash.',
    link: '/kusumoto',
  },
];

export default HomePage;